/* eslint-disable @typescript-eslint/no-dynamic-delete */
import { useView, PLATFORM, customElement, inject, TaskQueue, bindingMode, ValueAttributeObserver, EventSubscriber, computedFrom } from 'aurelia-framework';
import { IMdcTextFieldElement } from '@aurelia-mdc-web/text-field';
import { IValidatedElement, IError } from '@aurelia-mdc-web/base';
import { bindable } from 'aurelia-typed-observable-plugin';
import { parse } from 'date-fns/parse';
import { format } from 'date-fns/format';
import { InputmaskCustomAttribute } from 'aurelia-inputmask';
import { MdcDialogService } from '@aurelia-mdc-web/dialog';
import { MdcDaterangeDialog, IMdcDaterangeDialogData } from '../mdc-daterange-dialog/mdc-daterange-dialog';
import { IMdcDatepickerDialogOptions, MdcDatepickerDialogConfiguration } from '@aurelia-toolkit/mdc-datepicker';
import { getPredefinedIntervals } from '../mdc-daterange-dialog/get-predefined-intervals';
import { MdcMenuSurface } from '@aurelia-mdc-web/menu-surface';
import { addDays, addMonths, isSameDay, isSameMonth, isWeekend, lastDayOfMonth, startOfDay, startOfMonth } from '../../date-fns-local';
import { IPredefinedInterval } from './i-predefined-interval';
import { differenceInDays } from 'date-fns';

const DATE_ISO_FORMAT = 'yyyy-MM-dd';
const DATETIME_ISO_FORMAT = 'yyyy-MM-dd\'T\'HH:mm:ss';

interface IDay {
  date?: Date | null;
  disabled: boolean;
  inRange?: boolean;
  selectedFrom?: boolean;
  selectedTo?: boolean;
  hovered?: boolean;
}

interface IMonth {
  name: string;
  disabled: boolean;
}


@inject(Element, TaskQueue, MdcDialogService, MdcDatepickerDialogConfiguration)
@customElement('mdc-daterange-picker')
@useView(PLATFORM.moduleName('./mdc-daterange-picker.html'))
export class MdcDaterangePicker {
  constructor(private element: IMdcDateranePickerElement, private taskQueue: TaskQueue, private dialogService: MdcDialogService,
    private configuration: MdcDatepickerDialogConfiguration) {
    defineMdcDatepickerElementApis(this.element);
  }

  input: IMdcTextFieldElement;
  inputmask: InputmaskCustomAttribute;
  inputmaskValue: string;
  private _value: string;
  popup: MdcMenuSurface;
  weekDays: string[];
  from?: Date;
  to?: Date;
  fromMonth: number;
  fromYear: number;
  toMonth: number;
  toYear: number;
  years: number[];
  months: IMonth[];
  fromDays: IDay[];
  toDays: IDay[];
  options: Partial<IMdcDatepickerDialogOptions>;

  @bindable
  label: string;

  @bindable
  format: string = 'dd.MM.yyyy';

  @bindable
  inputmaskFormat: string = '99.99.9999 - 99.99.9999';

  @bindable
  dialogFormat: string = 'E, MMM d';

  @bindable.date
  min: Date;

  @bindable.date
  max: Date;

  @bindable
  disableFunction: (d: Date) => boolean;

  @bindable.booleanAttr
  disableWeekends: boolean;

  @bindable.booleanAttr
  showAll: boolean;

  @bindable.number
  firstDay: 0 | 1 | 2 | 3 | 4 | 5 | 6 = this.configuration.defaultOptions.firstDay as 0 | 1 | 2 | 3 | 4 | 5 | 6;

  @bindable
  mdcI18n: IMdcDatepickerDialogOptions['i18n'];

  @bindable.booleanAttr
  readonly: boolean;

  @bindable.booleanAttr
  time: boolean;

  predefinedIntervals = getPredefinedIntervals(this.firstDay);

  get value(): string {
    if (this.input) {
      const dates = this.getDates(this.inputmaskValue, this.format);
      return dates
        ? `${format(dates.from, this.getIsoFormat())} - ${format(dates.to, this.getIsoFormat())}`
        : '';
    } else {
      return this._value;
    }
  }

  set value(value: string) {
    this._value = value;
    if (this.input) {
      const dates = this.getDates(value, this.getIsoFormat());
      this.inputmaskValue = dates ? `${format(dates.from, this.format)} - ${format(dates.to, this.format)}` : '';
    }
  }

  @computedFrom('from', 'to')
  get numberOfDays() {
    if (!this.from || !this.to) return 0;
    return differenceInDays(this.to, this.from) + 1;
  }

  attached() {
    if (this.input) {
      this.value = this._value;
    }
    this.options = {
      label: this.label,
      format: this.dialogFormat,
      min: this.min,
      max: this.max,
      disableFunction: this.disableFunction,
      disableWeekends: this.disableWeekends,
      firstDay: isNaN(this.firstDay) ? undefined : this.firstDay,
      i18n: this.configuration.defaultOptions.i18n,
      showAll: this.showAll
    };
  }

  handleBlur(e: Event) {
    e.cancelBubble = true;
    this.element.dispatchEvent(new CustomEvent('blur', { bubbles: true }));
  }

  handleChange(e: Event) {
    e.cancelBubble = true;
    try {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const value = this.value;
      this.element.valid = true;
    } catch {
      this.element.valid = false;
    }
    this.taskQueue.queueTask(() => this.element.dispatchEvent(new CustomEvent('change', { bubbles: true })));
  }

  handleInput(e: Event) {
    e.cancelBubble = true;
    this.taskQueue.queueTask(() => this.element.dispatchEvent(new CustomEvent('input', { bubbles: true })));
  }

  getIsoFormat() {
    return this.time ? DATETIME_ISO_FORMAT : DATE_ISO_FORMAT;
  }

  getDates(value: string | undefined, format: string): { from: Date; to: Date } | undefined {
    if (!value) {
      return undefined;
    }
    const dateStrings = value.split(' - ');
    return { from: parse(dateStrings[0], format, new Date()), to: parse(dateStrings[1], format, new Date()) };
  }

  async open() {
    let value: string;
    try {
      value = this.value;
    } catch {
      value = '';
    }
    const data: Partial<IMdcDaterangeDialogData> = {
      ...this.getDates(value, this.getIsoFormat()),
      options: {
        label: this.label,
        format: this.dialogFormat,
        min: this.min,
        max: this.max,
        disableFunction: this.disableFunction,
        disableWeekends: this.disableWeekends,
        firstDay: isNaN(this.firstDay) ? undefined : this.firstDay,
        i18n: this.mdcI18n,
        showAll: this.showAll
      }
    };
    Object.keys(data.options).filter(key => data.options[key] === undefined).forEach(key => delete data.options[key]);
    if (this.mdcI18n) {
      Object.keys(data.options.i18n).filter(key => data.options.i18n[key] === undefined).forEach(key => delete data.options.i18n[key]);
    }
    this.element.dispatchEvent(new CustomEvent('open'));
    const result = await this.dialogService.open({ viewModel: MdcDaterangeDialog, model: data });
    this.element.dispatchEvent(new CustomEvent('close'));
    if (result === 'ok') {
      const value = `${format(data.from, this.getIsoFormat())} - ${format(data.to, this.getIsoFormat())}`;
      if (this.value !== value) {
        this.value = value;
        this.element.dispatchEvent(new CustomEvent('change', { bubbles: true }));
      }
    }
    this.input.focus();
  }

  openPopup() {
    this.popup.open = !this.popup.open;
    if (!this.popup.open) {
      return;
    }
    this.weekDays = Array.from({ length: 7 }, (_, i) => format(new Date(2020, 10, 16 + i - 1 + this.options.firstDay), 'EEE', { locale: this.options?.i18n?.dateFnsLocale }));
    const dates = this.getDates(this.value, this.getIsoFormat());
    this.from = dates.from ? startOfDay(dates.from) : undefined;
    this.to = dates.to ? startOfDay(dates.to) : undefined;
    this.init();
  }

  init() {
    if (this.min && this.from < this.min) {
      this.from = this.min;
    }
    if (this.max && this.from > this.max) {
      this.from = this.max;
    }
    const from = this.from ?? startOfDay(new Date());
    const to = this.to ?? addMonths(from, 1);
    this.fromMonth = from.getMonth();
    this.fromYear = from.getFullYear();
    if (isSameMonth(this.from, this.to)) {
      const startOfNextMonth = addMonths(startOfMonth(this.from), 1);
      this.toMonth = startOfNextMonth.getMonth();
      this.toYear = startOfNextMonth.getFullYear();
    } else {
      this.toMonth = to.getMonth();
      this.toYear = to.getFullYear();
    }
    this.buildMonthsYears();
    this.buildFromDays();
    this.buildToDays();
  }

  buildMonthsYears() {
    const min = Math.max(this.min ? this.min.getFullYear() : this.configuration.defaultOptions.yearRange.min, this.configuration.defaultOptions.yearRange.min);
    const max = Math.min(this.max ? this.max.getFullYear() : this.configuration.defaultOptions.yearRange.max, this.configuration.defaultOptions.yearRange.max);
    this.years = Array.from({ length: max - min + 1 }, (_, i) => min + i);
    this.months = Array.from({ length: 12 }, (_, i) => ({
      name: format(new Date(2020, i), 'LLLL', { locale: this.configuration.defaultOptions.i18n?.dateFnsLocale }),
      disabled: this.isMonthDisabled(this.fromYear, i)
    }));
  }

  isMonthDisabled(year: number, month: number) {
    return !!this.options.min && this.options.min > lastDayOfMonth(new Date(year, month))
      || !!this.options.max && this.options.max < new Date(year, month);
  }

  buildFromDays() {
    this.fromDays = this.buildDays(this.fromYear, this.fromMonth);
  }

  buildToDays() {
    this.toDays = this.buildDays(this.toYear, this.toMonth);
  }

  buildDays(toYear: number, toMonth: number): IDay[] {
    const first = new Date(toYear, toMonth);
    const offset = this.weekDays.indexOf(format(first, 'EEE', { locale: this.options.i18n?.dateFnsLocale }));
    return Array.from({ length: 42 }, (_, i) => {
      const date = addDays(first, i - offset);
      const sameMonth = isSameMonth(first, date);
      const day: IDay = {
        date: this.options.showAll || sameMonth ? date : null,
        disabled: this.isDayDisabled(date, sameMonth),
        selectedFrom: isSameDay(date, this.from),
        selectedTo: isSameDay(date, this.to)
      };
      day.inRange = !day.disabled && day.date && this.from <= date && date <= this.to;
      return day;
    });
  }

  isDayDisabled(date: Date, sameMonth: boolean): boolean {
    return !sameMonth
      || this.options.min && this.options.min > date
      || this.options.max && this.options.max < date
      || this.options.disableFunction?.(date)
      || this.options.disableWeekends && isWeekend(date);
  }

  monthSelected(month: number) {
    this.fromMonth = month;
  }

  select(d: IDay) {
    if (d.disabled || !d.date) {
      return;
    }

    // no days selected - set from
    if (!this.from && !this.to) {
      this.from = d.date;
      d.selectedFrom = true;
      return;
    }

    // only from selected
    if (this.from && !this.to) {
      if (d.date >= this.from) {
        this.to = d.date;
        d.selectedTo = true;
      } else {
        this.to = this.from;
        this.from = d.date;
        d.selectedFrom = true;
      }
      this.refreshDaysFlags();
      return;
    }

    // both dates selected
    if (this.from && this.to) {
      if (isSameMonth(this.from, this.to) && isSameMonth(this.from, d.date)) {
        this.from = d.date;
        this.to = undefined;
        this.refreshDaysFlags();
        return;
      } else {
        // depending on which calendar was selected set either from or to date
        if (this.fromDays.includes(d)) {
          this.from = d.date;
        } else {
          this.to = d.date;
        }
        this.refreshDaysFlags();
      }
      return;
    }
  }

  cancel() {
    this.popup.open = false;
  }

  ok() {
    const value = `${format(this.from, this.getIsoFormat())} - ${format(this.to, this.getIsoFormat())}`;
    if (this.value !== value) {
      this.value = value;
      this.element.dispatchEvent(new CustomEvent('change', { bubbles: true }));
    }
    this.popup.open = false;
  }

  @computedFrom('fromYear', 'fromMonth')
  get canGoPrevFrom() {
    if (!this.options) {
      return false;
    }
    const prevMonthStart = addMonths(new Date(this.fromYear, this.fromMonth), -1);
    return this.isMonthDisabled(prevMonthStart.getFullYear(), prevMonthStart.getMonth());
  }

  prevFrom() {
    const prevMonthStart = addMonths(new Date(this.fromYear, this.fromMonth), -1);
    [this.fromYear, this.fromMonth] = [prevMonthStart.getFullYear(), prevMonthStart.getMonth()];
  }

  @computedFrom('fromYear', 'fromMonth')
  get canGoNextFrom() {
    if (!this.options) {
      return false;
    }
    const nextMonthStart = addMonths(new Date(this.fromYear, this.fromMonth), 1);
    return this.isMonthDisabled(nextMonthStart.getFullYear(), nextMonthStart.getMonth());
  }

  nextFrom() {
    const nextMonthStart = addMonths(new Date(this.fromYear, this.fromMonth), 1);
    [this.fromYear, this.fromMonth] = [nextMonthStart.getFullYear(), nextMonthStart.getMonth()];
    if (this.fromYear === this.toYear && this.fromMonth === this.toMonth) {
      this.nextTo();
    }
  }

  @computedFrom('toYear', 'toMonth')
  get canGoPrevTo() {
    if (!this.options) {
      return false;
    }
    const prevMonth = addMonths(new Date(this.toYear, this.toMonth), -1);
    return this.isMonthDisabled(prevMonth.getFullYear(), prevMonth.getMonth());
  }

  prevTo() {
    const prevMonth = addMonths(new Date(this.toYear, this.toMonth), -1);
    [this.toYear, this.toMonth] = [prevMonth.getFullYear(), prevMonth.getMonth()];
    if (this.fromYear === this.toYear && this.fromMonth === this.toMonth) {
      this.prevFrom();
    }
  }

  @computedFrom('toYear', 'toMonth')
  get canGoNextTo() {
    if (!this.options) {
      return false;
    }
    const nextMonthStart = addMonths(new Date(this.toYear, this.toMonth), 1);
    return this.isMonthDisabled(nextMonthStart.getFullYear(), nextMonthStart.getMonth());
  }

  nextTo() {
    const nextMonthStart = addMonths(new Date(this.toYear, this.toMonth), 1);
    [this.toYear, this.toMonth] = [nextMonthStart.getFullYear(), nextMonthStart.getMonth()];
  }

  handleMouseover(d: IDay) {
    if (!this.from && !this.to || !d.date) {
      return;
    }
    this.refreshDaysFlags(d);
  }

  handleMouseLeave() {
    this.allDays().forEach(x => x.hovered = false);
  }

  private allDays() {
    return [...this.fromDays, ...this.toDays];
  }

  handleMenuSelected(pi: IPredefinedInterval) {
    this.from = pi.from;
    this.to = pi.to;
    this.init();
    this.ok();
  }

  refreshDaysFlags(hoveredDay?: IDay) {
    this.allDays().forEach(x => {
      x.selectedFrom = isSameDay(x.date, this.from);
      x.selectedTo = isSameDay(x.date, this.to);
      x.inRange = this.from <= x.date && x.date <= this.to;
      x.hovered = hoveredDay && (!this.from || !this.to)
        && !x.inRange
        && ((hoveredDay.date <= x.date && x.date <= (this.from ?? this.to))
          || ((this.from ?? this.to) <= x.date && x.date <= hoveredDay.date));
    });
  }

  clear() {
    this.from = undefined;
    this.to = undefined;
    this.refreshDaysFlags(undefined);
  }
}

export interface IMdcDateranePickerElement extends IValidatedElement {
  au: {
    controller: {
      viewModel: MdcDaterangePicker;
    };
  };
}

function defineMdcDatepickerElementApis(element: HTMLElement) {
  Object.defineProperties(element, {
    value: {
      get(this: IMdcDateranePickerElement): string {
        return this.au.controller.viewModel.value;
      },
      set(this: IMdcDateranePickerElement, value: string) {
        this.au.controller.viewModel.value = value;
      },
      configurable: true
    },
    valid: {
      get(this: IMdcDateranePickerElement) {
        return this.au.controller.viewModel.input.valid;
      },
      set(this: IMdcDateranePickerElement, value: boolean) {
        this.au.controller.viewModel.input.valid = value;
      },
      configurable: true
    },
    addError: {
      value(this: IMdcDateranePickerElement, error: IError) {
        this.au.controller.viewModel.input?.addError(error);
      },
      configurable: true
    },
    removeError: {
      value(this: IMdcDateranePickerElement, error: IError) {
        this.au.controller.viewModel.input?.removeError(error);
      },
      configurable: true
    },
    renderErrors: {
      value(this: IMdcDateranePickerElement): void {
        this.au.controller.viewModel.input?.renderErrors();
      },
      configurable: true
    }
  });
}

export const datepickerConfig = {
  tagName: 'mdc-daterange-picker',
  properties: {
    value: {
      defaultBindingMode: bindingMode.twoWay,
      getObserver(element: Element) {
        return new ValueAttributeObserver(element, 'value', new EventSubscriber(['change', 'input']));
      }
    }
  }
};
